import React, { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { faBooks, faHourglassHalf } from '@fortawesome/pro-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { RankmiTable } from '@rankmi/rankmi-table'
import { format } from 'date-fns'

import { faCheck } from '@fortawesome/pro-duotone-svg-icons'
import CommonToolTip from 'components/ui/tooltip/commonToolTip'
import { coursesFastActionsFilterOptions } from 'enum/coursesFastActionsOptions.enum'
import { CourseStateType } from 'enum'
import { canSkipOnRowSelected } from 'helpers'
import { IMainState } from 'store/states'
import { generateTableLocale } from 'helpers/translations.helper'
import ConfirmationModal from 'components/ui/modals/confirmation/ConfirmationModal'
import CourseState from 'components/courses/CourseState'
import { ExternalCourseIndicator } from 'components/ui/CourseCards/components/molecules/externalCourseIndicator'
import ProfilesCountCell from 'components/reusables/profilesCountCell'
import { IProfile } from 'interfaces/profiles'
import DuplicateCourseModal from '../duplicateCourseModal/DuplicateCourseModal'
import TrainingTypeTag from '../TrainingTypeTag'
import MissingDc3AttestationIcon from '../MissingDc3AttestationIcon'
import NormativeIncompleteParticipantsModal from '../NormativeIncompleteParticipantsModal'
import Options from './options/Options'
import styles from './index.module.scss'

interface ICourseList {
  setPage: any
  setSearch: any
  setSort: any
  setPerPage: any
  setSelectedRows: any
  setFastFilters: any
  setAllSelected: any
  onDelete: any
}
const CourseList: React.FC<ICourseList> = ({
  setPage,
  setSearch,
  setSort,
  setPerPage,
  setSelectedRows,
  setFastFilters,
  setAllSelected,
  onDelete,
}) => {
  const { t } = useTranslation()
  const [dataSource, setDataSource] = useState<any>([])
  const [showModal, setShowModal] = useState<any>(0)
  const [translations, setTranslations] = useState<any>({})
  const [showDeleteConfirmationModal, setShowDeleteConfirmationModal] =
    useState(false)
  const [normativeIncompleteModal, setNormativeIncompleteModal] = useState({
    open: false,
    incompleteCount: 0,
    courseId: 0,
  })
  const { list, total, loading } = useSelector(
    ({ courses }: IMainState) => courses
  )
  const attestationsEnabled = useSelector(
    ({ general }: IMainState) => general.permissions.attestationsEnabled
  )

  useEffect(() => {
    setTranslations(() => generateTableLocale())
  }, [])

  const [selected, setSelected] = useState({} as any)

  const prefix = 'administration.courses.list.create.table.headers'
  const nameColumnWidth = 300
  const trainingTypeColumnWidth = 140
  const profileColumnWidth = 130
  const stateColumnWidth = 140
  const synchronousColumnWidth = 85
  const createdAtColumnWidth = 150
  const actionColumnWidth = 45
  const checkboxColumnWidth = 48

  const tableScrollX = useMemo(
    () =>
      checkboxColumnWidth +
      nameColumnWidth +
      (attestationsEnabled ? trainingTypeColumnWidth : 0) +
      profileColumnWidth * 2 +
      synchronousColumnWidth +
      createdAtColumnWidth +
      stateColumnWidth +
      actionColumnWidth,
    [attestationsEnabled]
  )

  const columns = useMemo(() => {
    const cols: any[] = [
      {
        title: t(`${prefix}.name`),
        dataIndex: 'search_name',
        key: 'search_name',
        sorteable: true,
        hideable: false,
        width: nameColumnWidth,
        fixed: 'left',
        className: styles.nameColumn,
        render: (name: string, data: any) => {
          const showMissingDc3Icon =
            data.normative === true && data.dc3AttestationAssigned !== true

          return (
            <div
              className={
                attestationsEnabled && showMissingDc3Icon
                  ? `${styles.name} ${styles.nameWithDc3Icon}`
                  : styles.name
              }
              data-has-normative-icon={
                attestationsEnabled && showMissingDc3Icon ? 'true' : undefined
              }
            >
              {attestationsEnabled && showMissingDc3Icon ? (
                <MissingDc3AttestationIcon />
              ) : null}
              <div className={styles.nameText}>{name}</div>
              <span className={styles.nameIndicator}>
                <ExternalCourseIndicator
                  origin="TABLE"
                  url={data['integrationCourseLink']}
                />
              </span>
            </div>
          )
        },
      },
      {
        title: t(`${prefix}.forMandatoryProfiles`),
        dataIndex: 'mandatory',
        key: 'mandatory',
        width: profileColumnWidth,
        className: styles.profileColumn,
        render: (profiles: IProfile[], course: any) => (
          <div className={styles.profileCell}>
            <ProfilesCountCell
              resourceId={course.id}
              resourceType="course"
              kind="required"
              count={profiles?.length || 0}
            />
          </div>
        ),
      },
      {
        title: t(`${prefix}.forProfiles`),
        dataIndex: 'optional',
        key: 'optional',
        width: profileColumnWidth,
        className: styles.profileColumn,
        render: (profiles: IProfile[], course: any) => (
          <div className={styles.profileCell}>
            <ProfilesCountCell
              resourceId={course.id}
              resourceType="course"
              kind="optional"
              count={profiles?.length || 0}
            />
          </div>
        ),
      },
    ]

    if (attestationsEnabled) {
      cols.push({
        title: t(`${prefix}.trainingType.label`),
        dataIndex: 'normative',
        key: 'normative',
        width: trainingTypeColumnWidth,
        render: (normative: boolean) => (
          <TrainingTypeTag normative={normative === true} />
        ),
      })
    }

    cols.push(
      {
        title: t(`${prefix}.courseType.synchronous`),
        dataIndex: 'synchronous',
        key: 'synchronous',
        width: synchronousColumnWidth,
        render: (synchronous: boolean) =>
          synchronous ? (
            <FontAwesomeIcon
              icon={faCheck}
              style={{ color: '#36b37e', display: 'flex', margin: 'auto' }}
            />
          ) : null,
      },
      {
        title: t(`${prefix}.createAt`),
        sorteable: true,
        dataIndex: 'created_at',
        key: 'created_at',
        width: createdAtColumnWidth,
        render: (date: any) => format(new Date(date), 'dd-MM-yyyy kk:mm'),
      },
      {
        title: t(`${prefix}.state`),
        sorteable: true,
        dataIndex: 'state',
        key: 'state',
        width: stateColumnWidth,
        render: (state: CourseStateType) => <CourseState state={state} />,
      },
      {
        title: '',
        dataIndex: 'action',
        key: 'action',
        width: actionColumnWidth,
        hideable: false,
        align: 'center',
        fixed: 'right',
        className: styles.actionColumn,
        render: (
          id: any,
          {
            onDuplicated = false,
            name = '',
            integrationCourseLink,
            synchronous,
            normative,
            dc3AttestationAssigned,
          }: any
        ) =>
          !onDuplicated ? (
            <Options
              id={id}
              name={name}
              dc3AttestationAssigned={dc3AttestationAssigned === true}
              onDeleteClick={(id: number, name: string) =>
                onDeleteClick(id, name)
              }
              onDuplicateClick={(id: number) => onDuplicateClick(id)}
              integrationCourseLink={integrationCourseLink}
              synchronous={synchronous}
              normative={normative === true}
              onShowNormativeIncompleteModal={
                handleShowNormativeIncompleteModal
              }
            />
          ) : (
            <CommonToolTip
              text={t(
                'administration.courses.list.create.table.duplicateInProcess'
              )}
              position="left"
            >
              <FontAwesomeIcon
                icon={faHourglassHalf}
                className={styles.inProcess}
              />
            </CommonToolTip>
          ),
      }
    )

    return cols
  }, [attestationsEnabled, t])

  const fastAction = [
    {
      label: t(`commons.course.state.publishedCourses`),
      value: coursesFastActionsFilterOptions.publishedCourses,
    },
    {
      label: t(`commons.course.state.draftCourses`),
      value: coursesFastActionsFilterOptions.draftCourses,
    },
  ]

  useEffect(() => {
    setDataSource(
      list.map(
        ({
          id,
          name,
          state,
          requiredProfiles,
          optionalProfiles,
          createdAt,
          courseType,
          synchronous,
          integrationCourseLink,
          normative,
          dc3AttestationAssigned,
        }) => ({
          id,
          key: id,
          search_name: name,
          name,
          normative: normative ?? false,
          dc3AttestationAssigned: dc3AttestationAssigned === true,
          mandatory: requiredProfiles,
          optional: optionalProfiles,
          created_at: createdAt,
          synchronous,
          state,
          courseType,
          action: id,
          integrationCourseLink,
        })
      )
    )
  }, [list])

  const onDeleteClick = (id: number, name: string) => {
    setShowDeleteConfirmationModal(true)
    setSelected({ id, name })
  }

  const onDuplicateClick = (id: number) => {
    setShowModal(id)
  }

  const handlePreDelete = ({ id, name }: any) => {
    onDelete(id)
    setShowDeleteConfirmationModal(false)
    setSelected({ id, name })
  }

  const handleShowNormativeIncompleteModal = (
    incompleteCount: number,
    courseId: number
  ) => {
    setNormativeIncompleteModal({ open: true, incompleteCount, courseId })
  }

  return (
    <div
      className={styles.container}
      style={
        {
          '--course-name-column-width': `${nameColumnWidth}px`,
          '--course-profile-column-width': `${profileColumnWidth}px`,
          '--course-action-column-width': `${actionColumnWidth}px`,
        } as React.CSSProperties
      }
    >
      <RankmiTable
        columnHeaders={columns}
        data={dataSource}
        total={total}
        onPaginationChange={(page: any) => setPage(page)}
        onSearch={(text: string) => setSearch(text)}
        delaySearch
        onSortChange={(sortData: any) => setSort(sortData)}
        loading={loading}
        fastActions={fastAction}
        onRowsPerPageChange={setPerPage}
        onRowSelected={({
          selectedRowKeys,
          allSelected,
          unselectedRowKeys,
          dispatcher,
        }: any) => {
          if (canSkipOnRowSelected(dispatcher)) return
          setSelectedRows(allSelected ? unselectedRowKeys : selectedRowKeys)
          setAllSelected(allSelected)
        }}
        locale={translations}
        countIcon={faBooks}
        initialRowsPerPage={50}
        simpleSelectAll
        onFastActionSelect={(selected: any) => setFastFilters(selected)}
        responsive
        scroll={{ x: tableScrollX }}
      />
      {showDeleteConfirmationModal && (
        <ConfirmationModal
          show={showDeleteConfirmationModal}
          title=""
          text={t('administration.modal.courseDeleteConfirmation', {
            courseName: selected && selected.name.trim(),
          })}
          acceptButtonText={t('components.modal.buttons.delete')}
          onAccept={() => handlePreDelete(selected && selected)}
          onClose={() => setShowDeleteConfirmationModal(false)}
        />
      )}

      {!!showModal && (
        <DuplicateCourseModal
          show={showModal}
          onClose={() => setShowModal(0)}
        />
      )}

      <NormativeIncompleteParticipantsModal
        isOpen={normativeIncompleteModal.open}
        courseId={normativeIncompleteModal.courseId}
        incompleteCount={normativeIncompleteModal.incompleteCount}
        onClose={() =>
          setNormativeIncompleteModal({
            open: false,
            incompleteCount: 0,
            courseId: 0,
          })
        }
      />
    </div>
  )
}

export default CourseList
